import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import useAuthGuard from '../../hooks/useAuthGuard';
import type { AxiosError } from 'axios';
import {
  bulkUpdateQuestionStatus,
  deleteQuestion,
  fetchQuestionReferenceData,
  fetchQuestions,
} from '../../services/questionService';
import { fetchGroupTypes } from '../../services/taxonomyService';
import {
  QuestionListItem,
  QuestionListQueryParams,
  QuestionReferenceData,
  Status,
  GroupTypeResource,
  RichContent,
} from '../../types';

interface QuestionsFiltersState {
  keywords: string;
  status: Status | '';
  groupType: string;
  subjectId: string;
  topicId: string;
  subTopicId: string;
  uploadedBy: string;
  testSeriesId: string;
  startDate: string;
  endDate: string;
  tags: string[];
  notTags: string[];
}

const defaultFilters: QuestionsFiltersState = {
  keywords: '',
  status: '',
  groupType: '',
  subjectId: '',
  topicId: '',
  subTopicId: '',
  uploadedBy: '',
  testSeriesId: '',
  startDate: '',
  endDate: '',
  tags: [],
  notTags: [],
};

const stripHtml = (value: string): string =>
  value.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();

const truncate = (value: string | RichContent, length = 120): string => {
  let textContent = '';

  if (typeof value === 'string') {
    // Legacy string format
    textContent = value;
  } else if (value && typeof value === 'object') {
    // New rich content format
    textContent = value.text || '';
  } else {
    textContent = String(value || '');
  }

  const plain = stripHtml(textContent);
  return plain.length > length ? `${plain.slice(0, length)}…` : plain;
};

const formatDateTime = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

const toISODate = (value: string, type: 'start' | 'end'): string => {
  if (!value) {
    return '';
  }

  const suffix = type === 'start' ? 'T00:00:00.000Z' : 'T23:59:59.999Z';
  return new Date(`${value}${suffix}`).toISOString();
};

const htmlTagPattern = /<[a-z][^>]*>/i;

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const toRichHtml = (value: string | RichContent): string => {
  if (!value) {
    return '';
  }

  let textContent = '';
  let imageContent = '';

  if (typeof value === 'string') {
    // Legacy string format - check if it's an image URL
    const trimmed = value.trim();
    if (trimmed.match(/^\/uploads\/images\/[^\s]+$/)) {
      imageContent = trimmed;
    } else {
      textContent = trimmed;
    }
  } else if (value && typeof value === 'object') {
    // New rich content format
    textContent = value.text || '';
    imageContent = value.image || '';
    
    // Check if text content contains an image URL (possibly wrapped in HTML)
    if (!imageContent && textContent) {
      // Extract image URL from HTML or plain text
      const imageUrlMatch = textContent.match(/\/uploads\/images\/[a-f0-9-]+\.[a-z]+/);
      if (imageUrlMatch) {
        imageContent = imageUrlMatch[0];
        textContent = ''; // Clear text since it's just an image URL
      }
    }
  }

  if (!textContent && !imageContent) {
    return '';
  }

  let html = '';

  if (imageContent) {
    html += `<img src="${imageContent}" alt="Question image" style="width: 400px; height: 500px; object-fit: contain; margin-bottom: 8px; border: 1px solid #e0e0e0; border-radius: 4px;" />`;
  }

  if (textContent) {
    const normalized = textContent.trim();
    // Don't render if it's just HTML wrapping an image URL
    if (!normalized.match(/^<[^>]+>\s*\/uploads\/images\/[^<]+<\/[^>]+>$/)) {
      if (htmlTagPattern.test(normalized)) {
        html += normalized;
      } else {
        const escaped = escapeHtml(normalized);
        html += `<p>${escaped.replace(/\r?\n/g, '</p><p>')}</p>`;
      }
    }
  }

  return html;
};

const normalizeOptions = (options: unknown): string[] => {
  if (!options) {
    return [];
  }

  if (Array.isArray(options)) {
    return options
      .map((option, index) => {
        if (typeof option === 'string') {
          return toRichHtml(option);
        }

        if (option && typeof option === 'object') {
          const questionOption = option as Record<string, unknown>;
          
          // Handle nested content structure: { id: 'a', content: { text: '...', image: '...' } }
          if (questionOption.content && typeof questionOption.content === 'object') {
            return toRichHtml(questionOption.content as RichContent);
          }
          
          // Handle flat structure: { text: '...', image: '...' }
          const text = typeof questionOption.text === 'string' ? questionOption.text : '';
          const image = typeof questionOption.image === 'string' ? questionOption.image : '';

          return toRichHtml({
            text,
            image,
          });
        }

        return toRichHtml(`Option ${index + 1}`);
      })
      .filter((value) => value.length > 0);
  }

  return [toRichHtml(String(options))].filter((value) => value.length > 0);
};

const normalizeCorrectAnswer = (value: unknown): { content: string; isHtml: boolean } => {
  if (Array.isArray(value)) {
    const combined = value
      .map((entry) => toRichHtml(String(entry)))
      .filter((html) => html.length > 0)
      .join('<br />');

    return {
      content: combined || '',
      isHtml: combined.length > 0,
    };
  }

  if (typeof value === 'string') {
    const html = toRichHtml(value);
    return {
      content: html,
      isHtml: html.length > 0,
    };
  }

  if (typeof value === 'boolean') {
    return {
      content: value ? 'True' : 'False',
      isHtml: false,
    };
  }

  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (obj.text || obj.image) {
      const html = toRichHtml({ text: obj.text as string, image: obj.image as string });
      return {
        content: html,
        isHtml: html.length > 0,
      };
    }
    return {
      content: JSON.stringify(value, null, 2),
      isHtml: false,
    };
  }

  const content = value !== undefined && value !== null ? String(value) : '';
  return {
    content,
    isHtml: false,
  };
};

const QuestionsPage = () => {
  useAuthGuard(['admin', 'super_admin']);
  const navigate = useNavigate();

  const [questions, setQuestions] = useState<QuestionListItem[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [referenceData, setReferenceData] = useState<QuestionReferenceData | null>(null);
  const [referenceLoading, setReferenceLoading] = useState(true);
  const [referenceError, setReferenceError] = useState<string | null>(null);
  const [groupTypes, setGroupTypes] = useState<GroupTypeResource[]>([]);
  const [filters, setFilters] = useState<QuestionsFiltersState>(defaultFilters);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [viewQuestion, setViewQuestion] = useState<QuestionListItem | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<QuestionListItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const availableSubjects = useMemo(
    () => referenceData?.subjects ?? [],
    [referenceData]
  );
  const availableTopics = useMemo(() => {
    if (!filters.subjectId) {
      return [];
    }

    return availableSubjects.find((subject) => subject.id === filters.subjectId)?.topics ?? [];
  }, [availableSubjects, filters.subjectId]);

  const availableSubTopics = useMemo(() => {
    if (!filters.topicId) {
      return [];
    }

    return availableTopics.find((topic) => topic.id === filters.topicId)?.subTopics ?? [];
  }, [availableTopics, filters.topicId]);

  const tagOptions = useMemo(() => referenceData?.tags ?? [], [referenceData]);
  const testSeriesOptions = useMemo(() => referenceData?.testSeries ?? [], [referenceData]);

  const handleFilterChange = <K extends keyof QuestionsFiltersState>(
    key: K,
    value: QuestionsFiltersState[K]
  ) => {
    setFilters((prev) => {
      const next: QuestionsFiltersState = {
        ...prev,
        [key]: value,
      };

      if (key === 'subjectId') {
        next.topicId = '';
        next.subTopicId = '';
      }

      if (key === 'topicId') {
        next.subTopicId = '';
      }

      return next;
    });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    setFilters(defaultFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleRetryReference = () => {
    if (referenceLoading) {
      return;
    }
    loadReferenceData();
  };

  const loadQuestions = useCallback(
    async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);

    const params: QuestionListQueryParams = {
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      keywords: filters.keywords || undefined,
      status: filters.status || undefined,
      groupTypeId: filters.groupType || undefined,
      subjectId: filters.subjectId || undefined,
      topicId: filters.topicId || undefined,
      subTopicId: filters.subTopicId || undefined,
      uploadedBy: filters.uploadedBy || undefined,
      testSeriesId: filters.testSeriesId || undefined,
      startDate: filters.startDate ? toISODate(filters.startDate, 'start') : undefined,
      endDate: filters.endDate ? toISODate(filters.endDate, 'end') : undefined,
      tags: filters.tags.length ? filters.tags : undefined,
      notTags: filters.notTags.length ? filters.notTags : undefined,
    };

    try {
      const data = await fetchQuestions(params, signal);
      setQuestions((data && Array.isArray(data.items)) ? data.items : []);
      setTotalRows((data && typeof data.total === 'number') ? data.total : 0);
    } catch (err) {
        if (signal?.aborted) {
          return;
        }
      const message = err instanceof Error ? err.message : 'Failed to load questions';
      setError(message);
      setQuestions([]);
      setTotalRows(0);
    } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
    }
    },
    [filters, paginationModel.page, paginationModel.pageSize]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadQuestions(controller.signal);

    return () => {
      controller.abort();
    };
  }, [loadQuestions]);

  const loadReferenceData = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setReferenceLoading(true);
        setReferenceError(null);

        const [reference, groupTypes] = await Promise.all([
          fetchQuestionReferenceData(signal),
          fetchGroupTypes({ isActive: true }, signal),
        ]);

        if (signal?.aborted) {
          return;
        }

        setReferenceData(reference);
        setGroupTypes(groupTypes);
      } catch (referenceFetchError) {
        if (signal?.aborted) {
          return;
        }
        console.error('Failed to load question reference data', referenceFetchError);
        setReferenceData(null);
        setGroupTypes([]);
        const axiosError = referenceFetchError as AxiosError<{ message?: string }>;
        const message = axiosError.response?.data?.message ?? 'Failed to load filter reference data';
        setReferenceError(message);
      } finally {
        if (!signal?.aborted) {
          setReferenceLoading(false);
        }
      }
    }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadReferenceData(controller.signal);
    return () => controller.abort();
  }, [loadReferenceData]);

  useEffect(() => {
    if (!filters.topicId) {
      return;
    }

    const hasTopic = availableTopics.some((topic) => topic.id === filters.topicId);
    if (!hasTopic) {
      setFilters((prev) => ({
        ...prev,
        topicId: '',
        subTopicId: '',
      }));
    }
  }, [availableTopics, filters.topicId]);

  useEffect(() => {
    if (!filters.subTopicId) {
      return;
    }

    const hasSubTopic = availableSubTopics.some((subTopic) => subTopic.id === filters.subTopicId);
    if (!hasSubTopic) {
      setFilters((prev) => ({
        ...prev,
        subTopicId: '',
      }));
    }
  }, [availableSubTopics, filters.subTopicId]);

  const columns = useMemo<GridColDef[]>(() => {
    return [
      {
        field: 'rowNumber',
        headerName: '#',
        width: 60,
        sortable: false,
        valueGetter: (params) => {
          const relativeIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);
          return paginationModel.page * paginationModel.pageSize + relativeIndex + 1;
        },
      },
      {
        field: 'content',
        headerName: 'Question',
        flex: 2,
        minWidth: 200,
        renderCell: (params) => (
          <Typography variant="body2">{truncate(params.row.content)}</Typography>
        ),
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 120,
        valueGetter: (params) => params.row.type.replace(/_/g, ' '),
      },
      {
        field: 'groupType',
        headerName: 'Group',
        flex: 1,
        minWidth: 100,
        valueGetter: (params) => params.row.groupType?.name || '',
      },
      {
        field: 'uploadedBy',
        headerName: 'Uploaded By',
        flex: 1,
        minWidth: 120,
        valueGetter: (params) => params.row.uploadedBy?.name || '',
      },
      {
        field: 'createdAt',
        headerName: 'Date',
        width: 140,
        valueGetter: (params) => formatDateTime(params.row.createdAt),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={() => setViewQuestion(params.row)}
              title="View Question"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => navigate(`/questions/${params.row.id}/edit`)}
              disabled={actionLoading || deleteLoading}
              title="Edit Question"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeleteTarget(params.row)}
              disabled={actionLoading || deleteLoading}
              title="Delete Question"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ];
  }, [navigate, paginationModel.page, paginationModel.pageSize, actionLoading, deleteLoading]);

  const handleBulkStatusAction = async (nextStatus: Status) => {
    if (actionLoading) {
      return;
    }

    if (!rowSelectionModel.length) {
      setError('Select at least one question to update status');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      const { updatedCount } = await bulkUpdateQuestionStatus({
        ids: rowSelectionModel.map((id) => String(id)),
        status: nextStatus,
      });

      setSuccessMessage(
        updatedCount
          ? `Updated ${updatedCount} question${updatedCount === 1 ? '' : 's'} successfully`
          : 'No questions were updated'
      );
      setRowSelectionModel([]);
      await loadQuestions();
    } catch (updateError) {
      console.error('Failed to update question statuses', updateError);
      const axiosError = updateError as AxiosError<{ message?: string }>;
      const message = axiosError.response?.data?.message ?? 'Failed to update selected questions';
      setError(message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteQuestion = async () => {
    if (!deleteTarget || deleteLoading) {
      return;
    }

    try {
      setDeleteLoading(true);
      setError(null);
      await deleteQuestion(deleteTarget.id);
      setSuccessMessage('Question deleted successfully');
      setDeleteTarget(null);
      await loadQuestions();
    } catch (deleteError) {
      console.error('Failed to delete question', deleteError);
      const axiosError = deleteError as AxiosError<{ message?: string }>;
      const message = axiosError.response?.data?.message ?? 'Failed to delete question';
      setError(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Questions
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/questions/new')}
            size="small"
            sx={{ minWidth: 'auto' }}
          >
            Create Question
          </Button>
          <Button
            variant="outlined"
            disabled={!rowSelectionModel.length || actionLoading}
            onClick={() => handleBulkStatusAction('Active')}
            size="small"
          >
            Activate Selected
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={!rowSelectionModel.length || actionLoading}
            onClick={() => handleBulkStatusAction('Inactive')}
            size="small"
          >
            Deactivate Selected
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {referenceError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleRetryReference}
              disabled={referenceLoading}
            >
              Retry
            </Button>
          }
        >
          {referenceError}
        </Alert>
      )}

      <Card sx={{ mb: 4 }}>
        <CardContent>
          {referenceLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading filter reference data…
              </Typography>
            </Stack>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Keywords"
                value={filters.keywords}
                onChange={(event) => handleFilterChange('keywords', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={filters.status}
                  onChange={(event) => handleFilterChange('status', event.target.value as Status | '')}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="Draft">Draft</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Group Type</InputLabel>
                <Select
                  label="Group Type"
                  value={filters.groupType}
                  onChange={(event) => handleFilterChange('groupType', event.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {groupTypes.map((groupType) => (
                    <MenuItem key={groupType.id} value={groupType.id}>
                      {groupType.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Uploaded By (User ID)"
                value={filters.uploadedBy}
                onChange={(event) => handleFilterChange('uploadedBy', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={testSeriesOptions}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={testSeriesOptions.find((series) => series.id === filters.testSeriesId) ?? null}
                onChange={(_event, newValue) => handleFilterChange('testSeriesId', newValue?.id ?? '')}
                loading={referenceLoading && !testSeriesOptions.length}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Test Series"
                    placeholder="Select test series"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl
                fullWidth
                disabled={referenceLoading || availableSubjects.length === 0}
              >
                <InputLabel>Subject</InputLabel>
                <Select
                  label="Subject"
                  value={filters.subjectId}
                  onChange={(event) => handleFilterChange('subjectId', event.target.value)}
                >
                  <MenuItem value="">All Subjects</MenuItem>
                  {availableSubjects.map((subject) => (
                    <MenuItem key={subject.id} value={subject.id}>
                      {subject.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl
                fullWidth
                disabled={
                  referenceLoading || !filters.subjectId || availableTopics.length === 0
                }
              >
                <InputLabel>Topic</InputLabel>
                <Select
                  label="Topic"
                  value={filters.topicId}
                  onChange={(event) => handleFilterChange('topicId', event.target.value)}
                >
                  <MenuItem value="">All Topics</MenuItem>
                  {availableTopics.map((topic) => (
                    <MenuItem key={topic.id} value={topic.id}>
                      {topic.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl
                fullWidth
                disabled={
                  referenceLoading || !filters.topicId || availableSubTopics.length === 0
                }
              >
                <InputLabel>Sub Topic</InputLabel>
                <Select
                  label="Sub Topic"
                  value={filters.subTopicId}
                  onChange={(event) => handleFilterChange('subTopicId', event.target.value)}
                >
                  <MenuItem value="">All Sub Topics</MenuItem>
                  {availableSubTopics.map((subTopic) => (
                    <MenuItem key={subTopic.id} value={subTopic.id}>
                      {subTopic.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={filters.startDate}
                onChange={(event) => handleFilterChange('startDate', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                value={filters.endDate}
                onChange={(event) => handleFilterChange('endDate', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                options={tagOptions}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={tagOptions.filter((tag) => filters.tags.includes(tag.id))}
                onChange={(_event, selected) =>
                  handleFilterChange(
                    'tags',
                    selected.map((tag) => tag.id)
                  )
                }
                loading={referenceLoading && !tagOptions.length}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.name}
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    placeholder="Select tags"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                options={tagOptions}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={tagOptions.filter((tag) => filters.notTags.includes(tag.id))}
                onChange={(_event, selected) =>
                  handleFilterChange(
                    'notTags',
                    selected.map((tag) => tag.id)
                  )
                }
                loading={referenceLoading && !tagOptions.length}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.name}
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Exclude Tags"
                    placeholder="Select tags to exclude"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button color="inherit" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ height: { xs: 400, sm: 500, md: 640 }, width: '100%' }}>
        <DataGrid
          rows={questions ?? []}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSelectionModel}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50, 100]}
          getRowClassName={(params) => {
            switch (params.row.status) {
              case 'Active':
                return 'status-active';
              case 'Inactive':
                return 'status-inactive';
              case 'Draft':
                return 'status-draft';
              default:
                return '';
            }
          }}
          sx={{
            '& .MuiDataGrid-cell': {
              py: { xs: 1, sm: 1.5 },
            },
            '& .MuiDataGrid-columnHeader': {
              py: { xs: 1, sm: 1.5 },
            },
            '& .status-active': {
              backgroundColor: 'rgba(76, 175, 80, 0.08)', // Light green
            },
            '& .status-inactive': {
              backgroundColor: 'rgba(244, 67, 54, 0.08)', // Light red
            },
            '& .status-draft': {
              backgroundColor: 'rgba(255, 152, 0, 0.08)', // Light orange
            },
          }}
        />
      </Box>

      <Dialog
        fullWidth
        maxWidth="md"
        open={Boolean(viewQuestion)}
        onClose={() => setViewQuestion(null)}
      >
        <DialogTitle>Question Details</DialogTitle>
        <DialogContent dividers>
          {viewQuestion && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Question
                </Typography>
                <Typography
                  variant="body1"
                  component="div"
                  dangerouslySetInnerHTML={{ __html: toRichHtml(viewQuestion.content) }}
                />
              </Box>
              {viewQuestion.instructions && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Instructions
                  </Typography>
                  <Typography
                    variant="body2"
                    component="div"
                    dangerouslySetInnerHTML={{ __html: viewQuestion.instructions }}
                  />
                </Box>
              )}
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Question Metadata
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={`Type: ${viewQuestion.type.replace(/_/g, ' ')}`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                  <Chip
                    label={`Difficulty: ${viewQuestion.difficulty.replace(/_/g, ' ')}`}
                    size="small"
                    variant="outlined"
                    color="secondary"
                  />
                  <Chip
                    label={`Status: ${viewQuestion.status}`}
                    size="small"
                    variant="outlined"
                    color={viewQuestion.status === 'Active' ? 'success' : viewQuestion.status === 'Draft' ? 'warning' : 'default'}
                  />
                  <Chip
                    label={`Group: ${viewQuestion.groupType?.name || '—'}`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Uploaded By: ${viewQuestion.uploadedBy?.name || '—'}`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Subject: ${viewQuestion.subTopic?.topic?.subject?.name || '—'}`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Topic: ${viewQuestion.subTopic?.topic?.name || '—'}`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Sub Topic: ${viewQuestion.subTopic?.name || '—'}`}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Options
                </Typography>
                <Stack spacing={1}>
                  {normalizeOptions(viewQuestion.options).map((option, index) => (
                      <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {String.fromCharCode(65 + index)}.
                        </Typography>
                        <Typography
                          variant="body2"
                          component="div"
                          sx={{ flexGrow: 1 }}
                          dangerouslySetInnerHTML={{ __html: option }}
                        />
                      </Stack>
                    ))}
                </Stack>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Correct Answer
                </Typography>
                {(() => {
                  const formatted = normalizeCorrectAnswer(viewQuestion.correctAnswer);
                  if (formatted.isHtml) {
                    return (
                      <Typography
                        variant="body2"
                        component="div"
                        dangerouslySetInnerHTML={{ __html: formatted.content }}
                      />
                    );
                  }

                  return <Typography variant="body2">{formatted.content}</Typography>;
                })()}
              </Box>
              {viewQuestion.videoSolution && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Video Solution
                  </Typography>
                  <Typography variant="body2" color="primary.main">
                    {viewQuestion.videoSolution}
                  </Typography>
                </Box>
              )}
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {viewQuestion.tags?.map((tag) => (
                  <Chip key={tag.id} label={tag.name} size="small" />
                ))}
              </Stack>
              {!!(viewQuestion.testSeries && viewQuestion.testSeries.length) && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Test Series
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {viewQuestion.testSeries?.map((series) => (
                      <Chip key={series.id} label={series.name} size="small" />
                    ))}
                  </Stack>
                </Box>
              )}
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Additional Settings
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={`Static: ${viewQuestion.isStatic ? 'Yes' : 'No'}`}
                    size="small"
                    variant="outlined"
                    color={viewQuestion.isStatic ? 'success' : 'default'}
                  />
                  <Chip
                    label={`Show Solution: ${viewQuestion.showSolution ? 'Yes' : 'No'}`}
                    size="small"
                    variant="outlined"
                    color={viewQuestion.showSolution ? 'success' : 'default'}
                  />
                  <Chip
                    label={`Solving Time: ${viewQuestion.solvingTime}s`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Created: ${formatDateTime(viewQuestion.createdAt)}`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Updated: ${formatDateTime(viewQuestion.updatedAt)}`}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewQuestion(null)}>Close</Button>
          {viewQuestion && (
            <Button onClick={() => navigate(`/questions/${viewQuestion.id}/edit`)}>
              Edit Question
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleteLoading) {
            setDeleteTarget(null);
          }
        }}
      >
        <DialogTitle>Delete Question</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this question? This action cannot be undone.
          </Typography>
          {deleteTarget && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {truncate(deleteTarget.content, 140)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteQuestion}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4000}
        onClose={(_event, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          setSuccessMessage('');
        }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default QuestionsPage;